<script setup lang="ts">
import { datadogLogs } from "@datadog/browser-logs";
import * as Sentry from "@sentry/vue";
import { useIntersectionObserver, useMediaQuery } from "@vueuse/core";
import _ from "lodash";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Skeleton from "primevue/skeleton";
import { computed, type PropType, ref } from "vue";
import { useRoute } from "vue-router";
import ActionButton from "@/components/MyStudentDetail/ActionButton.vue";
import ActionCell from "@/components/MyStudents/DataCells/ActionCell.vue";
import type {
  AlgoliaStudentOrderExt,
  SubmittedResponse,
} from "@/components/MyStudents/MyStudentsAcceptance/MyStudentsAcceptanceModal.vue";
import MyStudentsAcceptanceModal from "@/components/MyStudents/MyStudentsAcceptance/MyStudentsAcceptanceModal.vue";
import { useSearchClient } from "@/composables/useSearchClient";
import { MAX_NOTE_TEXTAREA_LIMIT } from "@/constants";
import { parseAndFormatDateStringToDate } from "@/util/date";
import { useMyStudentsAlgoliaIndex } from "../composables/useMyStudentsAlgoliaIndex";
import TextCell from "../DataCells/TextCell.vue";
import { myStudentsDataTableTheme } from "../primeVueTheme";
import type { AlgoliaStudentOrder, Application } from "../types/algolia_student_orders";
import { DEFAULT_STUDENT_ORDER } from "../types/algolia_student_orders";
import { studentLink } from "../utils";
import StatusDropdown from "./StatusDropdown.vue";
import { ACCEPTANCE_STATUS } from "./utils.js";

const PROVISIONAL_TEXT_LIMIT: number = 1000;

interface UpdatedItem {
  id: string;
  status: string;
}

interface StatusRef {
  status: string;
}

const isLoading = ref<boolean>(true);

const emit = defineEmits(["update-totals"]);

const route = useRoute();
const sentinel = ref<HTMLDivElement | null>(null);

const props = defineProps({
  orders: {
    type: Array as PropType<AlgoliaStudentOrder[]>,
    default: [],
  },
  refineNext: {
    type: Function as PropType<() => void>,
    default: () => {},
  },
  isLastPage: {
    type: Boolean,
    default: false,
  },
});

const { getHitsPerPage } = useSearchClient();
const { sortOrder, selectedSortField } = useMyStudentsAlgoliaIndex();

useIntersectionObserver(sentinel, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    refineNextWithDebounce();
  }
});

/**
 * Debounced to prevent network storm from unresolved responses from Algolia
 */
const refineNextWithDebounce = _.debounce(() => {
  if (!props.isLastPage) {
    props.refineNext();
  } else {
    isLoading.value = false;
  }
}, 300);

const selectedApplications = ref<string[]>([]);
const isAcceptanceModalOpen = ref<boolean>(false);
const updatedItems = ref<UpdatedItem[]>([]);
const isViewMode = ref<boolean>(false);
const selectedItem = ref<AlgoliaStudentOrderExt>();

const isMobile = computed(() => useMediaQuery("(max-width: 768px)").value);

const noResultsText = "No Results Found.";

const formatDate = (date?: Date) => (date ? parseAndFormatDateStringToDate(date, "yyyy-MM-dd", "MM/dd/yyyy") : "");

const startAndEndDate = (startDate?: Date, endDate?: Date) =>
  startDate && endDate ? formatDate(startDate) + " - " + formatDate(endDate) : "";

const acceptanceItems = computed<AlgoliaStudentOrder[]>(() => {
  isLoading.value = true;
  const items = props.orders
    .map<AlgoliaStudentOrder | null>((record) => {
      if (updatedItems.value.some((item) => item.id === record.order.core.application_id)) {
        return null;
      }
      return record;
    })
    .filter<AlgoliaStudentOrder>((item) => !!item);

  if (!props.isLastPage) {
    items.push({ ...DEFAULT_STUDENT_ORDER, objectID: "skeleton" });
  }
  const hitsPerPage = getHitsPerPage();
  if (props.orders.length >= hitsPerPage && items.length < hitsPerPage) {
    refineNextWithDebounce();
  } else {
    isLoading.value = false;
  }
  isLoading.value = false;
  return items;
});

const handleSubmission = (submissionData: SubmittedResponse) => {
  const { success, data, error } = submissionData;
  if (success && data) {
    const filteredItems = updatedItems.value.filter((item) => item.id !== data.applicationId);
    updatedItems.value = [...filteredItems, { id: data.applicationId, status: data.payload.status }];
    const originalStatusItem = props.orders.filter((item) => item.order.core.application_id === data.applicationId);
    if (originalStatusItem.length > 0) {
      emit("update-totals", data.payload.status, originalStatusItem[0].order.core.host_accepted_status);
    }
  } else {
    datadogLogs.logger.error("Error while updating acceptance status for application", {
      data: data,
    });
    if (error) {
      Sentry.captureException(error, {
        tags: {
          troubleshooting_note: "Error while updating acceptance status for application.",
          application_id: data?.applicationId,
          ...(data?.payload ?? {}),
        },
      });
    }
  }
};

const closeModal = () => {
  isAcceptanceModalOpen.value = false;
  isViewMode.value = false;
};

const selectedMaxLength = computed<number>(() => {
  return selectedItem.value?.application?.host_accepted_status === ACCEPTANCE_STATUS.provisionallyAccepted
    ? PROVISIONAL_TEXT_LIMIT
    : MAX_NOTE_TEXTAREA_LIMIT;
});

const openModal = (item: AlgoliaStudentOrder, statusObj: StatusRef) => {
  selectedItem.value = structuredClone(item);
  selectedItem.value = {
    ...selectedItem.value,
    application: {
      id: selectedItem.value.order.core.application_id ?? "",
      host_accepted_status: statusObj.status,
    },
  };
  isAcceptanceModalOpen.value = true;
};

// Not implemented yet
const acceptAllSelected = async () => {
  const promises: Promise<void>[] = [];
  selectedApplications.value.forEach((application) => {
    promises.push(acceptApplication(application));
  });
  await Promise.allSettled(promises); // TODO maybe we can loop into failed promises to log something in Sentry
  selectedApplications.value = [];
};

const acceptApplication = async (application: string): Promise<void> => {
  // TODO Implement acceptance all in incoming ticket
};

const viewAcceptanceApplication = (data) => {
  selectedItem.value = data;
  isViewMode.value = true;
  isAcceptanceModalOpen.value = true;
};

const getHeaderCellClasses = (context, props) => {
  const headerCellConfig = myStudentsDataTableTheme.column.headercell({
    context,
    props,
  });
  return [...headerCellConfig.class, "border-gray-275", "text-university-primary"];
};

const getCheckboxCellClasses = (context, props) => ({
  class: [
    "flex items-center justify-center w-6 h-6 border transition-colors duration-100",
    context.checked ? "bg-blue-darker border-blue-darker" : "bg-white border-blue-darker",
    !props.disabled && !context.checked && "hover:border-blue-darker",
    !props.disabled && context.checked && "hover:bg-blue-darker hover:border-blue-darker",
    props.disabled && "cursor-default opacity-60",
  ],
});

const getStudentLink = (item) => {
  if (!item?.student) {
    return {};
  }
  const id = item?.student?.participant_id ?? "";
  return { ...studentLink(route, id) };
};

const myStudentsDataTableThemeOverride = {
  ...myStudentsDataTableTheme,
  column: {
    bodycell: (props) => {
      let base = "p-4 border-b border-gray-275";
      if (props.context.index === 1) {
        base = "p-4 border-b border-gray-275 text-teal-900 font-medium text-sm transition-colors duration-200";
      }
      return { class: base };
    },
    headercell: ({ context, props }) => ({
      class: getHeaderCellClasses(context, props),
    }),
    headercheckbox: {
      box: ({ props, context }) => getCheckboxCellClasses(context, props),
    },
    rowCheckbox: {
      box: ({ props, context }) => getCheckboxCellClasses(context, props),
    },
  },
};
</script>
<template>
  <div>
    <!-- setting to not show this button, bulk approvals looks working but we decided to take it out for now -->
    <ActionButton
      v-if="false"
      data-test-id="acceptance-all-button"
      button-text="ACCEPT"
      aria-label="Accept all selected items"
      :default-class="'flex !justify-start text-teal-700 p-4 w-full font-semibold rounded'"
      :loading-class="'flex !justify-start text-teal-700 p-4 w-full font-semibold rounded opacity-50 cursor-not-allowed'"
      :on-click-action="acceptAllSelected"
      :disabled="selectedApplications.length === 0"
    />
  </div>
  <data-table
    v-if="(orders ?? []).length > 0"
    v-model:selection="selectedApplications"
    data-testid="my-students-acceptance-results-list"
    :value="acceptanceItems"
    :rows="getHitsPerPage()"
    :sort-field="selectedSortField.columnField"
    :sort-order="sortOrder === 'asc' ? 1 : -1"
    :pt="myStudentsDataTableThemeOverride"
    :meta-key-selection="true"
    :table-style="'min-width: 768px; border-spacing: 0 !important;'"
    scroll-direction="horizontal"
    data-key="application.id"
  >
    <template #empty>{{ noResultsText }}</template>
    <!-- <column selection-mode="multiple" header-style="width: 3rem" /> -->
    <column header="Name and Email">
      <template #body="{ data, index }">
        <skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" />
        <router-link
          v-else
          class="min-h-full w-full"
          :to="{ ...studentLink(route, data.student.participant_id) }"
          :data-testid="`student-name-${index}`"
        >
          <text-cell
            :value="`${data.student.first_name_legal || data.student.first_name_preferred} ${data.student.last_name}`"
          />
          <text-cell custom-class="!text-teal-900" :value="data.student.email" />
        </router-link>
      </template>
    </column>
    <column header="Program Session Name">
      <template #body="{ data, index }">
        <skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" />
        <router-link
          v-else
          class="min-h-full w-full"
          :to="{ ...studentLink(route, data.student.participant_id) }"
          :data-testid="`program-session-${index}`"
        >
          <text-cell
            :value="data?.order?.session?.name ?? ''"
            custom-class="!text-teal-900 line-clamp-3"
          />
        </router-link>
      </template>
    </column>
    <column header="Program Dates">
      <template #body="{ data, index }">
        <skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" />
        <router-link
          v-else
          class="min-h-full w-full"
          :to="{ ...studentLink(route, data.student.participant_id) }"
          :data-testid="`program-date-${index}`"
        >
          <text-cell
            :value="startAndEndDate(data.order.session.academic_start_date, data.order.session.academic_end_date)"
          />
        </router-link>
      </template>
    </column>
    <column header="Application Deadline">
      <template #body="{ data, index }">
        <skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" />
        <router-link
          v-else
          class="min-h-full w-full"
          :to="{ ...studentLink(route, data.student.participant_id) }"
          :data-testid="`session-deadline-${index}`"
        >
          <text-cell :value="formatDate(data.order.session.deadline)" />
        </router-link>
      </template>
    </column>
    <column header="Applied Date">
      <template #body="{ data, index }">
        <skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" />
        <router-link
          v-else
          class="min-h-full w-full"
          :to="{ ...studentLink(route, data.student.participant_id) }"
          :data-testid="`applied-date-${index}`"
        >
          <text-cell :value="formatDate(data.order.core.created_at)" />
        </router-link>
      </template>
    </column>
    <column header="View Details" :pt="{ headercontent: 'flex items-center justify-center' }" :hidden="isMobile">
      <template #body="{ data }">
        <skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" />
        <action-cell
          v-else
          outlined
          :data-test-id="`acceptance-view-${data.order.core.application_id}`"
          :show-action-button="true"
          :data="data"
          :label="`View`"
          @clicked-event="viewAcceptanceApplication(data)"
        />
      </template>
    </column>
    <column
      header="Status"
      :pt="{
        headercontent: 'flex-1 !h-full items-center justify-center',
        headerCell: 'row-horizontal-shadow',
      }"
      body-class="bg-white row-horizontal-shadow items-center justify-center flex-1 !h-full"
      frozen
      align-frozen="right"
      class="frozen-column-right"
      style="min-width: 220px;"
    >
      <template #body="{ data }">
        <skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" />
        <StatusDropdown
          v-else
          :key="data.order.core.application_id"
          :data-test-id="`acceptance-status-${data.order.core.application_id}`"
          :status="data.order.core.host_accepted_status"
          :disabled="data.order.core.host_accepted_status === ACCEPTANCE_STATUS.accepted"
          @open-modal="(status:StatusRef) => openModal(data, status)"
        />
      </template>
    </column>
  </data-table>
  <div ref="sentinel" class="flex items-center justify-center w-full p-4" />
  <MyStudentsAcceptanceModal
    v-if="isAcceptanceModalOpen"
    :open="isAcceptanceModalOpen"
    :data="selectedItem"
    :max-length="selectedMaxLength"
    :is-view-mode="isViewMode"
    @close-modal="closeModal"
    @submitted="handleSubmission"
  />
</template>
